import { ASSETS, GAME } from "../data/content";
import GooglePlayBadge from "../components/GooglePlayBadge";
import useReveal from "../hooks/useReveal";

/**
 * Section 1 — Splash hero.
 * Full-bleed banner image with the game logo and a single "Get it on Google Play" CTA.
 * Designed to occupy most of the initial viewport.
 */
export default function SplashHero() {
  const r1 = useReveal();
  const r2 = useReveal();
  const r3 = useReveal();

  return (
    <section
      id="top"
      data-testid="splash-hero"
      className="relative isolate overflow-hidden min-h-[100svh] grain"
    >
      {/* Splash banner background */}
      <div className="absolute inset-0 -z-10">
        <img
          src={ASSETS.splashBanner}
          alt=""
          aria-hidden="true"
          className="hero-zoom h-full w-full object-cover object-[center_28%]"
        />
        {/* Lighter darkening so the banner art (animals + podium) reads clearly */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c0f0a]/30 via-[#0c0f0a]/10 to-[#0c0f0a]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_45%,_rgba(0,0,0,0.45)_100%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 min-h-[100svh] flex flex-col">
        {/* Top spacer to push content to vertical centre */}
        <div className="flex-1" />

        <div className="flex flex-col items-center text-center pb-24 lg:pb-28">
          {/* Game Logo — smaller so the banner animals remain the hero */}
          <div
            ref={r1}
            className="reveal float-soft"
            style={{ transitionDelay: "60ms" }}
          >
            <img
              src={ASSETS.gameLogo}
              alt={`${GAME.name} logo`}
              data-testid="game-logo"
              className="w-[180px] sm:w-[220px] md:w-[260px] lg:w-[300px] h-auto drop-shadow-[0_18px_50px_rgba(0,0,0,0.75)] select-none"
              draggable="false"
            />
          </div>

          {/* Tagline */}
          <p
            ref={r2}
            className="reveal mt-6 max-w-xl text-sm sm:text-base uppercase tracking-[0.32em] text-white/85 font-body font-medium [text-shadow:0_2px_12px_rgba(0,0,0,0.8)]"
            style={{ transitionDelay: "180ms" }}
            data-testid="splash-tagline"
          >
            The Premium Hunting Experience
          </p>

          {/* Single CTA */}
          <div
            ref={r3}
            className="reveal mt-8"
            style={{ transitionDelay: "320ms" }}
          >
            <GooglePlayBadge testId="splash-google-play-badge" size="lg" />
          </div>
        </div>

        {/* Scroll cue */}
        <div className="pb-8 flex justify-center">
          <span
            aria-hidden="true"
            className="inline-flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-white/45 font-body"
          >
            Scroll
            <span className="h-10 w-px bg-gradient-to-b from-white/60 to-transparent" />
          </span>
        </div>
      </div>
    </section>
  );
}
